import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { ClientManagementState } from "../types";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientDepartment, ClientJobRole } from "../api/clientManagement";

interface DefaultsTabProps {
  managementState: ClientManagementState;
  onChange: (field: keyof ClientManagementState, value: string | boolean) => void;
  defaultDepartmentsQuery: UseQueryResult<{ data: ClientDepartment[] }>;
  createDefaultDepartmentMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateDefaultDepartmentMutation: UseMutationResult<unknown, unknown, { departmentId: string; payload: { name?: string; is_active?: boolean } }>;
  deleteDefaultDepartmentMutation: UseMutationResult<unknown, unknown, string>;
  defaultJobRolesQuery: UseQueryResult<{ data: ClientJobRole[] }>;
  createDefaultJobRoleMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateDefaultJobRoleMutation: UseMutationResult<unknown, unknown, { jobRoleId: string; payload: { name?: string; is_active?: boolean } }>;
  deleteDefaultJobRoleMutation: UseMutationResult<unknown, unknown, string>;
}

// ---------------------------------------------------------------------------
// Generic list card used for both departments and job roles
// ---------------------------------------------------------------------------

interface EntityListCardProps<T extends { id: string; name: string; is_active?: boolean }> {
  title: string;
  icon: React.ReactNode;
  emptyLabel: string;
  addLabel: string;
  addDialogTitle: string;
  items: T[];
  isLoading: boolean;
  createMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateMutation: { isPending: boolean };
  deleteMutation: UseMutationResult<unknown, unknown, string>;
  onUpdate: (id: string, name: string, isActive: boolean) => void;
  onDelete: (id: string) => void;
}

function EntityListCard<T extends { id: string; name: string; is_active?: boolean }>({
  title,
  icon,
  emptyLabel,
  addLabel,
  addDialogTitle,
  items,
  isLoading,
  createMutation,
  updateMutation,
  deleteMutation,
  onUpdate,
  onDelete,
}: EntityListCardProps<T>) {
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addActive, setAddActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editActive, setEditActive] = useState(true);

  const filtered = items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));

  const handleOpenAdd = () => { setAddName(""); setAddActive(true); setAddOpen(true); };

  const handleConfirmAdd = () => {
    if (!addName.trim()) return;
    createMutation.mutate(
      { name: addName.trim(), is_active: addActive },
      { onSuccess: () => setAddOpen(false) }
    );
  };

  const handleStartEdit = (item: T) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditActive(item.is_active !== false);
  };

  const handleCancelEdit = () => { setEditingId(null); setEditName(""); };

  const handleSaveEdit = (id: string) => {
    onUpdate(id, editName, editActive);
    setEditingId(null);
  };

  return (
    <>
      <SectionCard
        title={title}
        description={`${items.length} ${items.length === 1 ? "record" : "records"}`}
        action={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            {addLabel}
          </Button>
        }
      >
        <TextField
          size="small"
          fullWidth
          placeholder={`Search ${title.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        <Divider />

        {isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Stack>
        ) : items.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <Box sx={{ fontSize: 36, opacity: 0.3, mb: 1 }}>{icon}</Box>
            <Typography variant="body2">No {title.toLowerCase()} yet.</Typography>
            <Typography variant="caption">Click "{addLabel}" to create one.</Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No results for "{search}"
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {filtered.map((item) => {
              const isEditing = editingId === item.id;
              const isActive = item.is_active !== false;
              return (
                <Box key={item.id} sx={{ py: 1.25 }}>
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim()) handleSaveEdit(item.id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Chip
                        label={editActive ? "Active" : "Inactive"}
                        size="small"
                        color={editActive ? "success" : "default"}
                        variant={editActive ? "filled" : "outlined"}
                        onClick={() => setEditActive((v) => !v)}
                        sx={{ cursor: "pointer" }}
                      />
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!editName.trim() || updateMutation.isPending}
                            onClick={() => handleSaveEdit(item.id)}
                          >
                            {updateMutation.isPending ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>{item.name}</Typography>
                      <Chip
                        label={isActive ? "Active" : "Inactive"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant="outlined"
                      />
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => handleStartEdit(item)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete "${item.name}"? This cannot be undone.`)) {
                              onDelete(item.id);
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{addDialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && addName.trim()) handleConfirmAdd(); }}
            />
            <FormControlLabel
              control={<Switch checked={addActive} onChange={(e) => setAddActive(e.target.checked)} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addName.trim() || createMutation.isPending}
            onClick={handleConfirmAdd}
          >
            {createMutation.isPending ? <CircularProgress size={18} /> : addLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// DefaultsTab
// ---------------------------------------------------------------------------

export const DefaultsTab: React.FC<DefaultsTabProps> = ({
  managementState,
  onChange,
  defaultDepartmentsQuery,
  createDefaultDepartmentMutation,
  updateDefaultDepartmentMutation,
  deleteDefaultDepartmentMutation,
  defaultJobRolesQuery,
  createDefaultJobRoleMutation,
  updateDefaultJobRoleMutation,
  deleteDefaultJobRoleMutation,
}) => {
  const departments = defaultDepartmentsQuery.data?.data ?? [];
  const roles = defaultJobRolesQuery.data?.data ?? [];

  return (
    <SettingsSection>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <EntityListCard
            title="Departments"
            icon={<BusinessIcon sx={{ fontSize: 36 }} />}
            emptyLabel="departments"
            addLabel="Add department"
            addDialogTitle="Add default department"
            items={departments}
            isLoading={defaultDepartmentsQuery.isLoading}
            createMutation={createDefaultDepartmentMutation}
            updateMutation={updateDefaultDepartmentMutation}
            deleteMutation={deleteDefaultDepartmentMutation}
            onUpdate={(id, name, isActive) =>
              updateDefaultDepartmentMutation.mutate({
                departmentId: id,
                payload: { name, is_active: isActive },
              })
            }
            onDelete={(id) => deleteDefaultDepartmentMutation.mutate(id)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <EntityListCard
            title="Job roles"
            icon={<WorkOutlineIcon sx={{ fontSize: 36 }} />}
            emptyLabel="job roles"
            addLabel="Add role"
            addDialogTitle="Add default job role"
            items={roles}
            isLoading={defaultJobRolesQuery.isLoading}
            createMutation={createDefaultJobRoleMutation}
            updateMutation={updateDefaultJobRoleMutation}
            deleteMutation={deleteDefaultJobRoleMutation}
            onUpdate={(id, name, isActive) =>
              updateDefaultJobRoleMutation.mutate({
                jobRoleId: id,
                payload: { name, is_active: isActive },
              })
            }
            onDelete={(id) => deleteDefaultJobRoleMutation.mutate(id)}
          />
        </Grid>
      </Grid>
    </SettingsSection>
  );
};
